// hooks/useShopifyAuth.js
import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import AuthContext from '../AuthContext';
import { useOrganizations } from './useOrganizations';
import TokenService from '../services/token.service';

// URL del API Gateway de Shopify
const API_BASE_URL = process.env.REACT_APP_CONNECTIONS_API_URL;

// URL de callback registrada en Shopify Partners
const SHOPIFY_CALLBACK_URL = process.env.REACT_APP_SHOPIFY_CALLBACK_URL;

export const useShopifyAuth = () => {
  const { user } = useContext(AuthContext);
  const { currentAccount, currentContext } = useOrganizations();
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [shopData, setShopData] = useState(null); // Datos de la tienda
  const [shopDomain, setShopDomain] = useState(null); // Dominio de la tienda conectada
  const [integrationId, setIntegrationId] = useState(null); // ID de la integración
  
  // Refs to prevent multiple concurrent requests
  const isCheckingStatus = useRef(false);
  const lastAccountId = useRef(null);
  
  // ✅ Use currentAccount.id from OrganizationContext for context-aware Shopify integration
  const userId = user?.user_id;
  const accountId = currentAccount?.id;
  const organizationId = currentContext?.type === 'ORGANIZATION' ? currentContext?.id : null;

  const checkStatus = useCallback(async () => {
    // Prevent multiple concurrent status checks
    if (isCheckingStatus.current) {
      return;
    }
    
    const token = TokenService.getAccessToken();
    if (!token) {
      setIsConnected(false);
      return;
    }

    isCheckingStatus.current = true;
    
    try {
      setIsLoading(true);
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userId || '',
        'x-account-id': accountId || ''
      };
      
      // Si estamos en contexto de organización, agregar el organization_id
      if (organizationId) {
        headers['x-organization-id'] = organizationId;
      }
      
      // Agregar shop_domain como query param según la especificación
      const statusUrl = shopDomain 
        ? `${API_BASE_URL}/shopify/auth/status?shop_domain=${encodeURIComponent(shopDomain)}`
        : `${API_BASE_URL}/shopify/auth/status`;
      
      const response = await fetch(statusUrl, {
        headers
      });
      
      if (response.ok) {
        let data = await response.json();
        
        // Si la respuesta tiene estructura de Lambda Proxy Response, extraer el body
        if (data.statusCode && data.body) {
          try {
            const parsedBody = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
            data = parsedBody.data || parsedBody;
          } catch (e) {
            console.warn('⚠️ [Shopify] Could not parse Lambda proxy body:', e);
          }
        }
        
        // Si la respuesta tiene una propiedad 'data', extraerla
        if (data.data && typeof data.data === 'object') {
          data = data.data;
        }
        
        // Verificar si hay conexión activa - SOLO confiar en el backend
        const connected = data.connected || data.is_connected || data.access_token || data.shop;
        setIsConnected(connected);
        
        if (connected) {
          localStorage.setItem('shopify_connected', 'true');
          
          // Usar datos reales del backend - manejar múltiples formatos de respuesta
          const shopInfo = data.shop || data.shop_info || data.shopData || {
            name: data.shop_name,
            domain: data.shop_domain
          };
          
          if (shopInfo && (shopInfo.name || shopInfo.domain)) {
            setShopData(shopInfo);
          }
          
          // También guardar el dominio si viene directamente
          if (data.shop_domain) {
            setShopDomain(data.shop_domain);
          }
          
          // Guardar el integration_id si viene en la respuesta
          if (data.integration_id) {
            setIntegrationId(data.integration_id);
          }
          
          // NO llamar fetchShopifyData - ya tenemos todos los datos del status
          // Limpiar estados de retry ya que la conexión es exitosa
          localStorage.removeItem('callback_processing_attempts');
          localStorage.removeItem('oauth_successful');
        } else {
          // Si el backend dice que NO está conectado, limpiar todo
          localStorage.removeItem('shopify_connected');
          setIsConnected(false);
          setShopData(null);
          setShopDomain(null);
        }
      } else {
        // Si el backend falla, asumir NO conectado (no usar localStorage)
        setIsConnected(false);
        setShopData(null);
        setShopDomain(null);
        localStorage.removeItem('shopify_connected');
      }
    } catch (error) {
      console.error('❌ [Shopify] Status check error:', error);
      // Si hay error, asumir NO conectado (no usar localStorage)
      setIsConnected(false);
      setShopData(null);
      setShopDomain(null);
      localStorage.removeItem('shopify_connected');
    } finally {
      setIsLoading(false);
      isCheckingStatus.current = false;
    }
  }, [userId, accountId, organizationId]);

  // 🔄 Reload Shopify data when account/organization context changes
  useEffect(() => {
    // Only check status if accountId changed (not on every render)
    if (accountId && accountId !== lastAccountId.current) {
      
      lastAccountId.current = accountId;
      
      // Clear previous account's data AND localStorage
      setIsConnected(false);
      setShopData(null);
      setShopDomain(null);
      setError(null);
      // Clear localStorage to avoid showing old account's data
      localStorage.removeItem('shopify_connected');
      localStorage.removeItem('shopify_orders');
      localStorage.removeItem('shopify_products');
      localStorage.removeItem('shopify_orders_timestamp');
      localStorage.removeItem('shopify_products_timestamp');
      // Also clear retry states
      localStorage.removeItem('callback_processing_attempts');
      localStorage.removeItem('oauth_successful');
      // Check status for new account/organization
      checkStatus();
    }
  }, [accountId, organizationId, checkStatus]);

  // 🔔 Escuchar mensajes del callback de Shopify (OAuth popup)
  useEffect(() => {
    const handleMessage = (event) => {
      // Filtrar mensajes irrelevantes
      if (event.data?.source === 'react-devtools-content-script' || 
          event.data?.source === 'react-devtools-bridge' ||
          !event.data?.type) {
        return;
      }
      
      if (event.data?.type === 'SHOPIFY_SUCCESS' || event.data?.type === 'SHOPIFY_AUTH_SUCCESS') {
        
        // Limpiar estados de retry
        localStorage.removeItem('callback_processing_attempts');
        localStorage.removeItem('oauth_successful');
        
        // Actualizar estado inmediatamente
        setError(null);
        setIsLoading(false);
        
        // Guardar en localStorage como backup
        localStorage.setItem('shopify_connected', 'true');
        
        // Verificar estado con el backend después de un pequeño delay
        setTimeout(() => {
          checkStatus();
        }, 500);
      } else if (event.data?.type === 'SHOPIFY_ERROR') {
        console.error('❌ [Shopify] OAuth error message received:', event.data);
        
        // Limpiar estados
        localStorage.removeItem('oauth_successful');
        localStorage.removeItem('shopify_connected');
        localStorage.removeItem('callback_processing_attempts');
        
        setIsLoading(false);
        setIsConnected(false);
        setError(`Error de OAuth: ${event.data.error_description || event.data.error}`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [checkStatus]);

  // Nueva función para obtener datos de Shopify
  // NOTA: Esta función ya no hace requests adicionales - checkStatus() ya obtiene todo
  // Solo se mantiene para compatibilidad con el código existente
  const fetchShopifyData = useCallback(async () => {
    // Simplemente llamar a checkStatus que ya tiene toda la lógica
    await checkStatus();
  }, [checkStatus]);

  const startAuth = useCallback(async (shopDomainInput) => {
    const token = TokenService.getAccessToken();
    if (!token) {
      
      setError('No authentication token found');
      return;
    }

    // Validar que se proporcione un dominio
    if (!shopDomainInput || shopDomainInput.trim() === '') {
      setError('Por favor, introduce el dominio de tu tienda de Shopify (ej: mi-tienda.myshopify.com)');
      return;
    }
    
    

    
    // VALIDACIÓN CRÍTICA: Verificar que tenemos los IDs
    if (!userId || !accountId) {
      
      
      
      
      setError('Error: No se pudieron obtener los datos del usuario. Por favor, cierra sesión y vuelve a entrar.');
      return;
    }
    
    
    

    
    // Formato correcto del dominio según la documentación
    let domain = shopDomainInput.trim();
    
    // Asegurar que termine en .myshopify.com
    if (!domain.includes('.myshopify.com')) {
      domain = `${domain}.myshopify.com`;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const url = `${API_BASE_URL}/shopify/auth/authorize`;
      
      

      // Request body según la especificación de la API
      const requestBody = {
        id: accountId, // ID del usuario/cuenta según la especificación
        shopName: domain,
        redirect_uri: SHOPIFY_CALLBACK_URL, // URL registrada en Shopify Partners
        scopes: [
          'read_products',
          'write_products',
          'read_orders', 
          'write_orders',
          'read_inventory',
          'write_inventory'
        ]
      };
      
      

      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userId || '',
        'x-account-id': accountId || ''
      };
      
      // Si estamos en contexto de organización, agregar el organization_id
      if (organizationId) {
        headers['x-organization-id'] = organizationId;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers,
        body: JSON.stringify(requestBody)
      });

      


      if (response.ok) {
        let data = await response.json();
        
        // Manejar respuesta de API Gateway con body como string JSON
        if (data.body && typeof data.body === 'string') {
          try {
            const parsedBody = JSON.parse(data.body);
            // Usar los datos del body parseado
            data = parsedBody.data || parsedBody;
          } catch (e) {
            console.error('❌ [Shopify] Error parsing body:', e);
          }
        } else if (data.data) {
          // Si la respuesta ya tiene estructura data.data
          data = data.data;
        }
        
        // Buscar la URL de autorización en diferentes campos posibles
        const authUrl = data.authorization_url || data.auth_url || data.authUrl || data.url;
        
        if (authUrl) {
        
          
          // Guardar token en sessionStorage para que el popup pueda accederlo
          sessionStorage.setItem('shopify_auth_token', token);
          sessionStorage.setItem('shopify_auth_user_id', userId || '');
          sessionStorage.setItem('shopify_auth_account_id', accountId || '');
          if (organizationId) {
            sessionStorage.setItem('shopify_auth_organization_id', organizationId);
          } else {
            sessionStorage.removeItem('shopify_auth_organization_id');
          }
        
          
          // Mostrar estado de "conectando"
          setIsLoading(true);
          setError(null);
          
          // Abrir popup con OAuth de Shopify directamente (no nuestro callback)
          const popup = window.open(
            authUrl,
            'shopify-auth',
            'width=800,height=600,left=200,top=200,scrollbars=yes,resizable=yes'
          );
          
          if (!popup) {
            
            setError('No se pudo abrir la ventana de autorización. Por favor, permite popups para este sitio.');
            setIsLoading(false);
            return;
          }
          
        
          
          // Escuchar mensaje de éxito del backend
          const handleMessage = (event) => {
            // Verificar origen - puede venir del backend (AWS) o del localhost  
            if (event.origin !== window.location.origin && 
                !event.origin.includes('execute-api.us-west-2.amazonaws.com') &&
                !event.origin.includes('localhost')) {
              
              return;
            }
            
            
            
            if (event.data.type === 'SHOPIFY_AUTH_SUCCESS') {
              
              
              
              // Marcar como exitoso
              localStorage.setItem('oauth_successful', 'true');
              localStorage.setItem('shopify_connected', 'true');
              
              // Limpiar datos temporales
              sessionStorage.removeItem('shopify_auth_token');
              sessionStorage.removeItem('shopify_auth_user_id');
              sessionStorage.removeItem('shopify_auth_account_id');
              
              // Actualizar estado inmediatamente
              setIsConnected(true);
              setError(null);
              setIsLoading(false);
              
              // Verificar estado desde el backend (ahora debería devolver connected: true)
              setTimeout(async () => {
                
                await fetchShopifyData();
              }, 1000);
              
              // Limpiar listeners
              window.removeEventListener('message', handleMessage);
              clearInterval(checkClosed);
            }
            
            if (event.data.type === 'SHOPIFY_AUTH_ERROR') {
              
              
              // Limpiar datos temporales
              sessionStorage.removeItem('shopify_auth_token');
              sessionStorage.removeItem('shopify_auth_user_id');
              sessionStorage.removeItem('shopify_auth_account_id');
              
              setIsLoading(false);
              setError(`Error de OAuth: ${event.data.error}`);
              
              // Limpiar listeners
              window.removeEventListener('message', handleMessage);
              clearInterval(checkClosed);
            }
          };
          
          window.addEventListener('message', handleMessage);
          
          // Monitorear el popup
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              
              
              // Verificar si recibimos confirmación de éxito del callback
              const wasOAuthSuccessful = localStorage.getItem('oauth_successful') === 'true';
              
              if (wasOAuthSuccessful) {
                
                setIsConnected(true);
                setError(null);
                setIsLoading(false);
                localStorage.setItem('shopify_connected', 'true');
                
                // Obtener datos inmediatamente
                setTimeout(() => fetchShopifyData(), 1000);
              } else {
                
                
                setIsLoading(false);
                
                // Verificar estado después de un momento por si acaso
                setTimeout(() => {
                  
                  fetchShopifyData();
                }, 3000);
              }
            }
          }, 1000);
          
        } else {
          setError('No se recibió URL de autorización válida');
        }
      } else {
        let errorMessage = `Error ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          
          
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      
      
      // Manejo específico de errores CORS
      if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
        setError('Error de CORS: El servidor necesita configurar Access-Control-Allow-Origin para localhost:3000');
      } else {
        setError('Error al conectar con Shopify. Revisa tu conexión.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, accountId]);

  const disconnect = useCallback(async () => {
    const token = TokenService.getAccessToken();
    if (!token) {
      
      return false;
    }
    
    // Necesitamos el shop_domain para revocar
    const currentShopDomain = shopDomain || shopData?.domain || 'zenit-test-shop.myshopify.com';
    
    try {
      setIsLoading(true);
      setError(null);
      
      
      
      
      
      
      // Request body según la especificación de la API
      const revokeUrl = `${API_BASE_URL}/shopify/auth/revoke`;
      
      const revokeBody = {
        id: accountId, // ID del usuario/cuenta según la especificación
        shop_domain: currentShopDomain,
        reason: 'user_requested' // Razón opcional
      };
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userId || '',
        'x-account-id': accountId || '',
        'x-shop-domain': currentShopDomain
      };
      
      // Si estamos en contexto de organización, agregar el organization_id
      if (organizationId) {
        headers['x-organization-id'] = organizationId;
      }
      
      const response = await fetch(revokeUrl, {
        method: 'DELETE',
        headers,
        body: JSON.stringify(revokeBody)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        
        // Limpiar todo el estado local
        setIsConnected(false);
        setShopData(null);
        setShopDomain(null);
        
        // Limpiar localStorage
        localStorage.removeItem('shopify_connected');
        localStorage.removeItem('oauth_successful');
        localStorage.removeItem('callback_processing_attempts');
        
        // Limpiar sessionStorage si existe
        sessionStorage.removeItem('shopify_auth_token');
        sessionStorage.removeItem('shopify_auth_user_id');
        sessionStorage.removeItem('shopify_auth_account_id');
        
        
        return true;
      } else {
        const errorText = await response.text();
        
        setError('Error al desconectar. Por favor, intenta de nuevo.');
        return false;
      }
    } catch (error) {
      
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userId, accountId, organizationId, shopDomain, shopData]);

  // Validar token de Shopify según la especificación de la API
  const validateToken = useCallback(async (shopDomainToValidate = null) => {
    const token = TokenService.getAccessToken();
    if (!token) {
      return { valid: false, error: 'No authentication token found' };
    }

    const domainToValidate = shopDomainToValidate || shopDomain || shopData?.domain;
    
    if (!domainToValidate) {
      return { valid: false, error: 'No shop domain available' };
    }

    try {
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userId || '',
        'x-account-id': accountId || ''
      };
      
      if (organizationId) {
        headers['x-organization-id'] = organizationId;
      }

      // Request body según la especificación de la API
      const validateBody = {
        id: accountId,
        shop_domain: domainToValidate
      };

      const response = await fetch(`${API_BASE_URL}/shopify/auth/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(validateBody)
      });

      if (response.ok) {
        const data = await response.json();
        return { valid: true, data };
      } else if (response.status === 401) {
        // Token inválido - limpiar estado
        setIsConnected(false);
        setShopData(null);
        localStorage.removeItem('shopify_connected');
        return { valid: false, error: 'Token is invalid or expired' };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return { valid: false, error: errorData.message || `Validation failed: ${response.status}` };
      }
    } catch (error) {
      console.error('❌ [Shopify] Token validation error:', error);
      return { valid: false, error: error.message };
    }
  }, [userId, accountId, organizationId, shopDomain, shopData]);

  // Verificar estado al cargar si hay usuario y token
  useEffect(() => {
    if (user && TokenService.getAccessToken()) {
      
      setTimeout(() => {
        // Llamar checkStatus sin dependencias para evitar problemas de inicialización
        checkStatus();
      }, 1500); // Un poco más de delay para asegurar que todo esté listo
    }
  }, [userId, accountId, checkStatus]);

  return {
    // Estado de autenticación
    isConnected,
    isLoading,
    error,
    
    // Información de la tienda
    shopData,        // Datos de la tienda (nombre, plan, etc.)
    shopDomain,      // Dominio de la tienda (ej: mi-tienda.myshopify.com)
    integrationId,   // ID de la integración para workflows
    
    // Acciones de autenticación
    checkStatus,     // Verificar estado de conexión
    startAuth,       // Iniciar flujo OAuth
    disconnect,      // Desconectar tienda
    validateToken    // Validar token de Shopify
  };
};
